import { useRef, useMemo } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { testimonials } from '../data/menuData';

// Animation variants defined outside component
const fadeInUpVariants = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 }
};

const transitionConfig = { duration: 0.6 };
const summaryTransition = { duration: 0.6, delay: 0.6 };

// Star Rating Component
const StarRating = ({ count }) => (
  <div className="flex gap-1 mb-4">
    {[...Array(count)].map((_, i) => (
      <Star key={i} size={20} className="fill-bakery-primary text-bakery-primary" />
    ))}
  </div>
);

// Five Stars Component
const FiveStars = () => (
  <div className="flex">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={24} className="fill-bakery-primary text-bakery-primary" />
    ))}
  </div>
);

// Testimonial Card Component
const TestimonialCard = ({ testimonial, index, isInView }) => {
  const slideVariants = useMemo(() => ({
    initial: { opacity: 0, x: index % 2 === 0 ? -30 : 30 },
    animate: { opacity: 1, x: 0 }
  }), [index]);

  const transition = useMemo(() => ({ 
    duration: 0.6, 
    delay: index * 0.15 
  }), [index]);

  return (
    <motion.div
      variants={slideVariants}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
      transition={transition}
      className="bg-bakery-bg dark:bg-gray-900 rounded-2xl p-8 relative testimonial-card"
      data-testid={`testimonial-${testimonial.id}`}
    >
      <div className="absolute top-6 right-6 text-bakery-primary/20">
        <Quote size={48} />
      </div>
      <StarRating count={testimonial.rating} />
      <p className="text-bakery-text/80 dark:text-bakery-bg/80 text-lg leading-relaxed mb-6 relative z-10">
        &ldquo;{testimonial.text}&rdquo;
      </p>
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-bakery-primary flex items-center justify-center">
          <span className="text-white font-bold text-lg">
            {testimonial.name.charAt(0)}
          </span>
        </div>
        <div>
          <h4 className="font-semibold text-bakery-text dark:text-bakery-bg">{testimonial.name}</h4>
          <p className="text-sm text-bakery-text/60 dark:text-bakery-bg/60">{testimonial.date}</p>
        </div>
      </div>
    </motion.div>
  );
};

const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const displayedTestimonials = useMemo(() => 
    testimonials.length > 4 ? testimonials.slice(0, 4) : testimonials
  , []);

  return (
    <section id="testimonials" className="py-20 md:py-32 bg-white dark:bg-gray-800" data-testid="testimonials-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          variants={fadeInUpVariants}
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          transition={transitionConfig}
          className="text-center mb-16"
        >
          <span className="font-accent text-2xl text-bakery-primary">What People Say</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-bakery-text dark:text-bakery-bg mt-2">
            Customer <span className="text-bakery-primary">Reviews</span>
          </h2>
          <p className="text-bakery-text/70 dark:text-bakery-bg/70 mt-4 max-w-2xl mx-auto">
            Don&apos;t just take our word for it - hear what our happy customers have to say
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {displayedTestimonials.map((testimonial, index) => (
            <TestimonialCard 
              key={testimonial.id} 
              testimonial={testimonial} 
              index={index} 
              isInView={isInView} 
            />
          ))}
        </div>

        {/* Rating summary */}
        <motion.div
          variants={fadeInUpVariants}
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          transition={summaryTransition}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-4 bg-bakery-muted dark:bg-gray-900 px-8 py-4 rounded-full">
            <FiveStars />
            <span className="font-heading text-2xl font-bold text-bakery-text dark:text-bakery-bg">5.0</span>
            <span className="text-bakery-text/70 dark:text-bakery-bg/70">Based on customer reviews</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
